import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { v4 as uuidv4 } from 'uuid';
import { authApi } from '../api/auth-api';
import { STORAGE_KEYS } from '@/utils/constants';
import { setAuthGate } from '@/lib/api-client';
import type { MontaGuestUser, AuthStatus } from '../types/auth.types';

interface AuthState {
  // State
  user: MontaGuestUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  authStatus: AuthStatus;
  sessionId: string;
  isInitialized: boolean;
  error: Error | null;

  // Actions
  initialize: () => Promise<void>;
  signUpGuest: () => Promise<void>;
  _signUpGuest: () => Promise<void>;
  checkUser: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: MontaGuestUser) => void;
  logout: () => void;
  clearError: () => void;
}

// In-flight guest sign-up, shared so concurrent 401s mint one token, not N.
let signUpInFlight: Promise<void> | null = null;

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      immer((set, get) => ({
        // Initial state
        user: null,
        accessToken: null,
        refreshToken: null,
        authStatus: 'loading',
        sessionId: uuidv4(),
        isInitialized: false,
        error: null,

        // Initialize auth on app startup. Doesn't block rendering: API
        // requests wait on the auth gate (see api-client) instead.
        initialize: async () => {
          const { accessToken, checkUser, signUpGuest, isInitialized } = get();

          // Prevent multiple initializations
          if (isInitialized) {
            return;
          }

          try {
            set({ error: null });

            if (accessToken) {
              // Use the persisted token right away and validate it in the
              // background; a 401 anywhere re-mints and retries (api-client).
              set({ isInitialized: true });
              void checkUser().catch(() => signUpGuest().catch(() => {}));
            } else {
              // No token, sign up as guest
              await signUpGuest();
              set({ isInitialized: true });
            }
          } catch (error) {
            // Gracefully handle initialization failure
            console.error('Failed to initialize auth:', error);
            set({
              isInitialized: true,
              authStatus: 'noUser',
              error:
                error instanceof Error
                  ? error
                  : new Error('Failed to initialize authentication'),
            });
          }
        },

        // Sign up as guest user
        signUpGuest: () => {
          signUpInFlight ??= get()
            ._signUpGuest()
            .finally(() => {
              signUpInFlight = null;
            });
          setAuthGate(signUpInFlight.catch(() => {}));
          return signUpInFlight;
        },

        _signUpGuest: async () => {
          try {
            set({ authStatus: 'loading', error: null });
            const response = await authApi.signUpGuest();

            const user: MontaGuestUser = {
              id: response.id,
              language: response.language,
              token: response.token,
              country: response.country,
              lastCheck: new Date().toISOString(),
            };

            set({
              user,
              accessToken: response.token.access_token,
              refreshToken: response.token.refresh_token,
              authStatus: 'authenticated',
            });

            // Store tokens in localStorage
            localStorage.setItem(
              STORAGE_KEYS.AUTH_TOKEN,
              response.token.access_token
            );
            localStorage.setItem(
              STORAGE_KEYS.REFRESH_TOKEN,
              response.token.refresh_token
            );
          } catch (error) {
            console.error('Failed to sign up guest:', error);
            set({
              authStatus: 'noUser',
              error:
                error instanceof Error
                  ? error
                  : new Error('Failed to authenticate'),
            });
            throw error;
          }
        },

        // Check if current user is valid
        checkUser: async () => {
          try {
            const response = await authApi.getUserMe();
            const { user } = get();

            if (user) {
              set((state) => {
                if (state.user) {
                  state.user.id = response.id;
                  state.user.language = response.language;
                  state.user.country = response.country;
                  state.user.lastCheck = new Date().toISOString();
                }
                state.authStatus = 'authenticated';
              });
            }
          } catch (error) {
            // 401/403 will be caught here and trigger re-authentication
            console.error('Failed to check user:', error);
            set({ authStatus: 'noUser' });
            throw error;
          }
        },

        // Set tokens
        setTokens: (accessToken: string, refreshToken: string) => {
          set({ accessToken, refreshToken });
          localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
        },

        // Set user
        setUser: (user: MontaGuestUser) => {
          set({ user, authStatus: 'authenticated' });
        },

        // Logout
        logout: () => {
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            authStatus: 'noUser',
            error: null,
          });
          localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        },

        // Clear error
        clearError: () => {
          set({ error: null });
        },
      })),
      {
        name: 'monta-auth',
        partialize: (state) => ({
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          user: state.user,
          sessionId: state.sessionId,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);

// Listen for unauthorized events
if (typeof window !== 'undefined') {
  window.addEventListener('auth:unauthorized', () => {
    const { signUpGuest } = useAuthStore.getState();
    void signUpGuest();
  });
}
